<template>
  <div
    :style="updatePost ? { backgroundImage: 'url(/img/cloud-bg.jpg)' } : {}"
    class="lg:ml-56 bg-bluegray-700 bg-center bg-cover"
  >
    <!-- Post header -->
    <div
      class="px-4 pt-24 w-11/12 m-auto d-block text-white"
      :class="[userAccess && !updatePost ? 'pb-48' : 'pb-52']"
    >
      <h1 v-if="userAccess && updatePost">Edit Post</h1>
      <button
        v-if="userAccess && updatePost"
        class="flex space-x-1 uppercase cursor-pointer text-gray-100 hover:text-white"
        @click="editPost"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M7 16l-4-4m0 0l4-4m-4 4h18"
          />
        </svg>
        <span>View post</span>
      </button>

      <button
        v-if="!updatePost"
        class="flex space-x-1 uppercase cursor-pointer text-gray-100 hover:text-white"
        @click="goBack"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M7 16l-4-4m0 0l4-4m-4 4h18"
          />
        </svg>
        <span>Back</span>
      </button>

      <button
        v-if="userAccess && !updatePost"
        :disabled="!loaded"
        type="button"
        class="btn btn-black mt-4 flex space-x-1"
        @click="editPost"
      >
        <span>Edit</span> <span v-if="page">page</span><span v-else>post</span>
      </button>
    </div>

    <!-- Post content -->
    <div
      class="bg-bluegray-100 w-full pb-16 flex flex-col items-center lg:items-start"
    >
      <div
        class="lg:min-h-post overflow-hidden rounded-xl lg:ml-16 -mt-48 relative z-20 px-6 lg:p-16 flex w-11/12 lg:w-9/12 transition-all duration-500"
        :class="[
          userAccess ? 'pb-16 pt-8 ' : 'py-16',
          loading ? 'bg-bluegray-500 items-center justify-center' : 'bg-white',
          loading && !mounting ? 'delay-500' : '',
        ]"
      >
        <PostContent v-if="!updatePost" :post_id="post_id" :page="page" />
        <PostUpdate
          v-if="userAccess && updatePost && !page"
          :post-id="post_id"
        />
        <div
          v-if="loading"
          class="h-48 w-48 absolute right-1/2 bottom-1/2 transform translate-x-1/2 translate-y-1/2"
        >
          <lottie :options="loadingAnimationOptions" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { EventBus } from '../../../js/plugins/event-bus.js'
import { shared } from '../../../js/mixins/shared.js'
import { mapActions, mapGetters } from 'vuex'
import PostContent from './PostContent.vue'
import PostUpdate from '../manager/PostUpdate.vue'
import * as loadingAnimation from '../../../shared/components/loading.json'

export default {
  components: {
    PostContent,
    PostUpdate,
  },

  mixins: [shared],

  props: {
    post_id: {
      type: Number,
      required: false,
      default: 0,
    },

    edit: {
      type: Boolean,
      required: false,
      default: false,
    },

    page: { default: false },
  },

  data() {
    return {
      loadingAnimationOptions: { animationData: loadingAnimation.default },
      loadingAnimate: false,
      thinking: false,
      window: {
        width: 0,
        height: 0,
      },

      updatePost: false,
      post: {},
      related: [],
      related_posts: [],
      categories: ['Page', 'News', 'Event', 'Job'],
      loading: false,
      loaded: false,

      mounting: true,
    }
  },

  computed: {
    ...mapGetters({
      settings: 'shared/getSettings',
      client: 'shared/getClient',
      userAccess: 'shared/getUserAccess',
      templateView: 'shared/getTemplateView',
    }),
  },

  watch: {
    post_id: {
      handler: function (id) {
        this.getPost(id)
      },

      deep: true,
    },

    page: {
      handler: function (val) {
        if (val) {
          this.$set(this, 'updatePost', false)
        }
      },

      deep: true,
    },
  },

  created() {
    window.addEventListener('resize', this.handleResize)
    this.handleResize()
  },

  mounted() {
    this.$set(this, 'mounting', true)
    this.getPost(this.post_id)

    EventBus.$on('post-toggle-editing', () => {
      this.updatePost = !this.updatePost
    })

    EventBus.$on('get_new_post_data', (id) => {
      this.getPost(id)
    })

    EventBus.$on('update_post_success', (id) => {
      let update = true
      //Reset current post data as otherwise post_id and store current post id are the same leading to ui glitches in public template load
      this.dispatchSetCurrentPost()
      this.getPost(id, update)
      this.$set(this, 'updatePost', false)
    })
  },

  unmounted() {
    window.removeEventListener('resize', this.handleResize)
  },

  methods: {
    ...mapActions({
      dispatchSetCurrentPost: 'shared/dispatchSetCurrentPost',
    }),

    editPost() {
      if (this.page) {
        this.$router.push({ path: '/manager/page/' + this.post_id })
      } else {
        EventBus.$emit('post-toggle-editing')
      }
    },

    handleResize() {
      this.window.width = window.innerWidth
      this.window.height = window.innerHeight
    },

    goBack() {
      if (Path === '/') {
        this.$router.push({ path: '/posts' })
      } else {
        this.$router.go(-1)
      }
    },

    getPost(id, update = false) {
      this.$set(this, 'loading', true)

      this.$set(this, 'loaded', false)

      let endpoint =
        this.client === 'client2tenant' && this.templateView !== 'Page'
          ? '/api/post-with-relic/' + id
          : '/api/post/' + id

      axios.get(endpoint).then((response) => {
        let result = response.data

        this.$set(this, 'loading', false)

        if (result.success) {
          this.$set(this, 'loaded', true)

          // handle tags
          let general_arr = []
          this.settings.tags.forEach((i) => {
            if (result.post.tag_titles.includes(i.text)) {
              general_arr.push(i.text)
            }
          })
          result.post.general_tags = general_arr

          let industry_arr = []
          this.settings.tag_industries.forEach((i) => {
            if (result.post.tag_titles.includes(i.text)) {
              industry_arr.push(i.text)
            }
          })
          result.post.industry_tags = industry_arr

          let year_arr = []
          this.settings.tag_years.forEach((i) => {
            if (result.post.tag_titles.includes(i.text)) {
              year_arr.push(i.text)
            }
          })
          result.post.year_tag = year_arr

          let region_arr = []
          this.settings.tag_regions.forEach((i) => {
            if (result.post.tag_titles.includes(i.text)) {
              region_arr.push(i.text)
            }
          })
          result.post.region_tag = region_arr

          if (result.post.related.length > 0) {
            this.$set(this, 'related', result.post.related)
          }

          this.dispatchSetCurrentPost(result.post)

          // check route params for edit status
          if (this.edit && !update) {
            this.$set(this, 'updatePost', true)
          }

          EventBus.$emit('newPostData')
        } else {
          //...
          this.$router.push({ path: '/posts' })
        }
      })
    },
  },
}
</script>

<style scoped>
.post-intro::after {
  content: '';
  background: #fff;
  height: 1px;
  width: 500px;
  min-width: 95%;
  position: absolute;
  top: 4rem;
  right: 0;
}
</style>
